// assets/composables/useInvestmentChart.js
import { ref, computed, nextTick } from 'vue'
import { formatAmount } from '@/lib/utils'

function calculateXIRR(cashFlows) {
    if (cashFlows.length < 2) return null
    let hasPositive = false, hasNegative = false
    for (const cf of cashFlows) {
        if (cf.amount > 0) hasPositive = true
        if (cf.amount < 0) hasNegative = true
    }
    if (!hasPositive || !hasNegative) return null
    const d0 = cashFlows[0].date.getTime()
    const xnpv = (rate) => cashFlows.reduce((sum, cf) => {
        const days = (cf.date.getTime() - d0) / 86400000
        return sum + cf.amount / Math.pow(1 + rate, days / 365.25)
    }, 0)
    let low = -0.999999, high = 100000
    let fLow = xnpv(low), fHigh = xnpv(high)
    if (Math.sign(fLow) === Math.sign(fHigh)) return Math.abs(fHigh) < Math.abs(fLow) ? high : low
    let rate = 0
    for (let i = 0; i < 100; i++) {
        rate = (low + high) / 2
        const val = xnpv(rate)
        if (Math.abs(val) < 1e-5) return rate
        if (Math.sign(val) === Math.sign(fLow)) { low = rate; fLow = val } else { high = rate }
    }
    return rate
}

export function useInvestmentChart(realizedMovements, investmentCurrentValue) {
    const isChartFullscreen = ref(false)
    const displayMode = ref('chart')
    const chartKey = ref(0)

    const toggleFullscreen = () => {
        isChartFullscreen.value = !isChartFullscreen.value
        nextTick(() => { chartKey.value++ })
    }

    const annualizedRate = computed(() => {
        if (!realizedMovements.value.length) return null
        const cashFlows = []
        realizedMovements.value.forEach(m => {
            let impact = 0
            if (m.subMovements && m.subMovements.length > 0) {
                m.subMovements.forEach(sub => {
                    if (sub.impactsInvestedAmount)
                        impact += sub.type === 'receipt' ? parseFloat(sub.amount) : -parseFloat(sub.amount)
                })
            } else {
                if (m.impactsInvestedAmount)
                    impact += m.type === 'receipt' ? parseFloat(m.amount) : -parseFloat(m.amount)
            }
            if (Math.abs(impact) > 1e-5) cashFlows.push({ amount: impact, date: new Date(m.date) })
        })
        if (cashFlows.length === 0) return null
        cashFlows.push({ amount: investmentCurrentValue.value, date: new Date() })
        cashFlows.sort((a, b) => a.date - b.date)
        const daysDiff = (cashFlows[cashFlows.length - 1].date - cashFlows[0].date) / 86400000
        if (daysDiff < 1) return null
        try {
            const rate = calculateXIRR(cashFlows)
            return rate !== null ? rate * 100 : null
        } catch { return null }
    })

    const CHART_COLORS = [
        'rgba(16,185,129,0.4)', 'rgba(245,158,11,0.4)', 'rgba(59,130,246,0.4)',
        'rgba(236,72,153,0.4)', 'rgba(139,92,246,0.4)', 'rgba(14,165,233,0.4)', 'rgba(244,63,94,0.4)',
    ]

    const chartData = computed(() => {
        if (!realizedMovements.value.length) return { labels: [], datasets: [] }
        const sorted = [...realizedMovements.value].sort((a, b) => new Date(a.date) - new Date(b.date))
        const labels = []
        const dataPoints = []
        const categoryTotals = {}
        const categoriesHistory = {}
        const allCategories = new Set()

        sorted.forEach(m => {
            if (m.subMovements && m.subMovements.length > 0) {
                m.subMovements.forEach(sub => { if (sub.type === 'payment') allCategories.add(sub.category ? sub.category.name : 'Sans catégorie') })
            } else {
                if (m.type === 'payment') allCategories.add(m.category ? m.category.name : 'Sans catégorie')
            }
        })
        allCategories.forEach(cat => { categoryTotals[cat] = 0; categoriesHistory[cat] = [] })

        let cumulative = 0
        sorted.forEach(m => {
            labels.push(m.date)
            let cvImpact = 0
            if (m.subMovements && m.subMovements.length > 0) {
                m.subMovements.forEach(sub => {
                    if (sub.impactsCurrentValue) cvImpact += (sub.type === 'receipt' ? -parseFloat(sub.amount) : parseFloat(sub.amount))
                    if (sub.type === 'payment') categoryTotals[sub.category ? sub.category.name : 'Sans catégorie'] += parseFloat(sub.amount)
                })
            } else {
                if (m.impactsCurrentValue) cvImpact += (m.type === 'receipt' ? -parseFloat(m.amount) : parseFloat(m.amount))
                if (m.type === 'payment') categoryTotals[m.category ? m.category.name : 'Sans catégorie'] += parseFloat(m.amount)
            }
            cumulative += cvImpact
            dataPoints.push(cumulative)
            allCategories.forEach(cat => categoriesHistory[cat].push(categoryTotals[cat]))
        })

        const datasets = []
        let i = 0
        allCategories.forEach(cat => {
            const color = CHART_COLORS[i % CHART_COLORS.length]
            datasets.push({ label: `Investi: ${cat}`, data: categoriesHistory[cat], backgroundColor: color, borderColor: color.replace('0.4', '1'), fill: true, tension: 0.1, stack: 'Investissements', order: 1 })
            i++
        })
        datasets.push({ label: 'Valorisation Cumulée (€)', data: dataPoints, borderColor: '#0f172a', backgroundColor: 'transparent', tension: 0.3, borderWidth: 2, pointRadius: 3, order: 0 })
        return { labels, datasets }
    })

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'top' }, title: { display: false } },
        scales: { y: { stacked: true } }
    }

    return { isChartFullscreen, displayMode, chartKey, toggleFullscreen, annualizedRate, chartData, chartOptions, formatAmount }
}
